(() => {
  if (customElements.get('hub-guide')) return;
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const unique = values => [...new Set(values)].sort((a,b) => a.localeCompare(b,document.documentElement.lang,{numeric:true}));
  class HubGuide extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      this.fields = Object.fromEntries(['make','model','year','month'].map(k=>[k,this.querySelector(`[data-field="${k}"]`)]));
      this.results = this.querySelector('[data-results]');
      this.status = this.querySelector('[data-status]');
      this.showResults = this.querySelector('[data-show-results]');
      this.showResults?.addEventListener('click',()=>{
        if(this.showResults.disabled)return;
        this.status.focus({preventScroll:true});
        this.status.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth',block:'start'});
      });
      this.revision = 0;
      this.init();
    }
    async init() {
      try {
        const response = await fetch(this.dataset.source);
        if (!response.ok) throw new Error('Data unavailable');
        this.data = await response.json();
        this.fill('make',unique(this.data.fitments.map(r=>r.make)));
        this.fields.make.disabled = false;
        this.addEventListener('change',e=>{if(e.target.matches('[data-field]'))this.change(e.target.dataset.field);});
        this.querySelector('[data-reset]').addEventListener('click',()=>{this.fields.make.value='';this.change('make');this.fields.make.focus();});
        this.status.textContent=this.labels.start;
      } catch { this.status.textContent=this.labels.error; }
    }
    fill(key,values) {
      this.fields[key].replaceChildren(new Option(this.labels['choose_'+key],''),...values.map(v=>new Option(v,v)));
    }
    choice() {return Object.fromEntries(Object.entries(this.fields).map(([k,f])=>[k,f.value]));}
    change(key) {
      if(key==='make') {
        this.fill('model',unique(this.data.fitments.filter(r=>r.make===this.fields.make.value).flatMap(r=>r.models)));
        this.fields.model.disabled=!this.fields.make.value;
      }
      if(key==='make'||key==='model') {
        const years=HubEngine.availableYears(this.data,this.fields.make.value,this.fields.model.value);
        this.fill('year',years.years.map(String));
        if(years.hasUnspecified)this.fields.year.add(new Option(this.labels.unspecified,'unspecified'));
        this.fields.year.disabled=!this.fields.model.value;
      }
      if(key!=='month')this.fields.month.value='';
      const c=this.choice();
      const needsMonth=this.data.fitments.some(r=>r.make===c.make&&r.models.includes(c.model)&&[r.start,r.end].some(d=>d?.month&&d.year===Number(c.year)));
      this.querySelector('[data-month]').hidden=!needsMonth;
      if(this.showResults)this.showResults.disabled=!(c.make&&c.model&&c.year);
      this.render();
    }
    period(row) {
      const format=d=>d?.year?String(d.year)+(d.month?'-'+String(d.month).padStart(2,'0'):''):this.labels.unknown;
      return `${format(row.start)} – ${format(row.end)}`;
    }
    async render() {
      const revision=++this.revision, c=this.choice();
      this.results.replaceChildren();
      this.results.removeAttribute('aria-busy');
      if(!c.make||!c.model||!c.year){this.status.textContent=this.labels.start;return;}
      const groups=new Map();
      for(const row of HubEngine.assess(this.data,c)){if(!groups.has(row.mpn))groups.set(row.mpn,[]);groups.get(row.mpn).push(row);}
      this.status.textContent=groups.size?(groups.size===1?this.labels.results_one:this.labels.results).replace('[count]',groups.size):this.labels.empty;
      this.results.setAttribute('aria-busy','true');
      await Promise.all([...groups].map(async([mpn,rows])=>{
        const article=document.createElement('article');
        article.className='hub-guide__result';
        const vehicle=`${escape(c.make)} ${escape(c.model)}${rows[0].series?' · '+escape(rows[0].series):''}`;
        article.innerHTML=`<header class="hub-guide__result-header"><h2 class="body-large">${vehicle}</h2></header><div class="hub-guide__result-body"><div class="hub-guide__specs">${rows.map(r=>`<table class="body-small"><tbody><tr><th scope="row">${escape(this.labels.product)}</th><td data-product-cell>${escape(this.labels.loading)}</td></tr><tr><th scope="row">${escape(this.labels.part)}</th><td>${escape(mpn)}</td></tr><tr><th scope="row">${escape(this.labels.period)}</th><td><strong>${escape(this.period(r))}</strong></td></tr><tr><th scope="row">${escape(this.labels.construction)}</th><td>${escape(this.labels[r.construction]||this.labels.unknown)}</td></tr><tr><th scope="row">${escape(this.labels.airbag)}</th><td>${escape(this.labels['airbag_'+r.airbag])}</td></tr></tbody></table>`).join('')}${rows.some(r=>r.reasons.length)?`<p class="hub-guide__notice body-small">${escape(this.labels.check)}</p>`:''}${rows.some(r=>r.reasons.includes('choose_month'))?`<p class="hub-guide__notice body-small">${escape(this.labels.month_help)}</p>`:''}</div><div class="hub-guide__purchase" data-purchase><p class="body-small">${escape(this.labels.loading)}</p></div></div>`;
        this.results.append(article);
        const slot=article.querySelector('[data-purchase]'), product=this.data.products[mpn];
        try {
          if(!product?.handle)throw new Error('No product');
          const url=new URL(`${window.Shopify?.routes?.root||'/'}products/${encodeURIComponent(product.handle)}`,location.origin);
          url.searchParams.set('view','hub-guide');
          url.searchParams.set('variant',product.variantId);
          const response=await fetch(url);
          if(!response.ok)throw new Error('Not on storefront');
          const doc=new DOMParser().parseFromString(await response.text(),'text/html');
          const card=doc.querySelector('[data-hub-product]');
          if(!card||card.dataset.variantId!==product.variantId||card.dataset.mpn.trim().toUpperCase()!==mpn)throw new Error('Mapping changed');
          if(revision!==this.revision)return;
          article.querySelectorAll('[data-product-cell]').forEach(cell=>{
            const link=document.createElement('a');
            link.href=card.dataset.productUrl;
            link.textContent=card.dataset.productTitle;
            cell.replaceChildren(link);
          });
          // Unique form IDs also when the section is used twice in the editor.
          card.querySelectorAll('form[id]').forEach(form=>{
            // Shopify's input named "id" shadows HTMLFormElement.id.
            const old=form.getAttribute('id'), next=`${this.id}-${old}`;
            form.setAttribute('id',next);
            card.querySelectorAll('[form]').forEach(button=>{if(button.getAttribute('form')===old)button.setAttribute('form',next);});
          });
          slot.replaceChildren(document.importNode(card,true));
        } catch {
          if(revision!==this.revision)return;
          article.querySelectorAll('[data-product-cell]').forEach(cell=>{cell.textContent=this.labels.product_unavailable;});
          slot.innerHTML=`<p>${escape(this.labels.unavailable)}</p><a class="btn btn--text" href="${escape(this.dataset.contact)}">${escape(this.labels.contact)}</a>`;
        }
      }));
      if(revision===this.revision)this.results.removeAttribute('aria-busy');
    }
  }
  customElements.define('hub-guide',HubGuide);
})();


